import type { ItemFieldValue } from '@/lib/api/item-store/field-values';
import type { CustomFieldTypes } from '@/lib/api/types/customField';
import type {
  CustomFieldValueDto,
  Item,
  ItemCustomFieldValue,
  ItemCustomFieldValues,
  ItemLinkValue,
  ItemMultiLinkValue,
  ItemV2,
} from '@/lib/api/types/items';
import { isCustomFieldValueDto } from '@/lib/api/types/items';

let nextSyntheticId = -Date.now();
const synthesizeId = (): number => --nextSyntheticId;

const MULTI_FIELD_TYPES = new Set([
  'MULTI_CUSTOMER',
  'MULTI_PICKLIST',
  'MULTI_ITEM',
  'MULTI_USER',
  'MULTI_TEXT',
  'MULTI_LINK',
]);

export function reconcileMultiValues(
  fieldType: string,
  fieldValue: ItemFieldValue['fieldValue'],
): unknown[] {
  if (fieldType === 'MULTI_LINK') {
    return fieldValue.map(({ id, value, description }): ItemMultiLinkValue => {
      const entry: ItemMultiLinkValue = { id, MULTI_LINK: value as string };
      if (description !== undefined) {
        entry.description = description;
      }
      return entry;
    });
  }
  return fieldValue.map(({ id, value }) => ({ id, [fieldType]: value }));
}

export function reconcileFieldValue(
  fieldType: string,
  fieldValue: ItemFieldValue['fieldValue'],
): ItemCustomFieldValue | undefined {
  if (MULTI_FIELD_TYPES.has(fieldType)) {
    return reconcileMultiValues(fieldType, fieldValue) as ItemCustomFieldValue;
  }
  const first = fieldValue[0];
  if (!first) return undefined;
  if (fieldType === 'LINK') {
    const linkValue: ItemLinkValue = { LINK: first.value as string };
    if (first.description !== undefined) linkValue.description = first.description;
    return linkValue;
  }
  return { [fieldType]: first.value } as ItemCustomFieldValue;
}

type RawEntry = Record<string, unknown>;

function isLegacyV1Single(rawValue: unknown, fieldType: CustomFieldTypes): boolean {
  return (
    typeof rawValue === 'object' &&
    rawValue !== null &&
    !Array.isArray(rawValue) &&
    fieldType in rawValue
  );
}

function unwrapSingleInput(rawValue: unknown): {
  value: unknown;
  description: string | undefined;
} {
  if (isCustomFieldValueDto(rawValue)) {
    return { value: rawValue.value, description: rawValue.description ?? undefined };
  }
  return { value: rawValue, description: undefined };
}

function toMultiEntry(
  fieldType: CustomFieldTypes,
  entry: unknown,
  index: number,
): RawEntry | unknown {
  if (typeof entry !== 'object' || entry === null) {
    return { id: synthesizeId(), [fieldType]: entry };
  }
  const obj = entry as RawEntry;
  if (fieldType in obj) return obj;

  const id = (obj.id as number | undefined) ?? synthesizeId() - index;
  if (fieldType === 'MULTI_LINK' && 'url' in obj) {
    return { id, MULTI_LINK: obj.url, description: obj.description };
  }
  if ('value' in obj) {
    const base: RawEntry = { id, [fieldType]: obj.value };
    if (fieldType === 'MULTI_LINK' && obj.description !== undefined) {
      base.description = obj.description;
    }
    return base;
  }
  return { id, [fieldType]: obj[fieldType] };
}

function parseDateValue(inner: unknown): { DATE: string } | undefined {
  if (typeof inner === 'string') return { DATE: inner };
  if (typeof inner !== 'object' || inner === null) return undefined;
  const { year, month, day } = inner as { year?: number; month?: number; day?: number };
  if (year === undefined || month === undefined || day === undefined) return undefined;
  return { DATE: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` };
}

export function customFieldV2ToV1(
  rawValue: unknown,
  fieldType: CustomFieldTypes,
): ItemCustomFieldValue | undefined {
  if (rawValue === null || rawValue === undefined) return undefined;
  if (isLegacyV1Single(rawValue, fieldType)) return rawValue as ItemCustomFieldValue;

  if (MULTI_FIELD_TYPES.has(fieldType)) {
    if (!Array.isArray(rawValue)) return undefined;
    return rawValue.map((entry, index) =>
      toMultiEntry(fieldType, entry, index),
    ) as ItemCustomFieldValue;
  }

  const { value, description } = unwrapSingleInput(rawValue);
  if (value === null || value === undefined) return undefined;

  switch (fieldType) {
    case 'CHECKBOX':
      return { CHECKBOX: value as boolean };
    case 'TEXT':
      return { TEXT: value as string };
    case 'MULTILINE_TEXT':
      return { MULTILINE_TEXT: value as string };
    case 'NOTE':
      return { NOTE: value as string };
    case 'NUMERIC':
      return { NUMERIC: value as number };
    case 'CURRENCY':
      return { CURRENCY: value as number };
    case 'PICKLIST':
      return { PICKLIST: value as number };
    case 'ITEM':
      return { ITEM: value as number };
    case 'USER':
      return { USER: value as string };
    case 'DATE':
      return parseDateValue(value);
    case 'LINK': {
      const link: ItemLinkValue = { LINK: value as string };
      if (description !== undefined) link.description = description;
      return link;
    }
    default:
      return undefined;
  }
}

type V2FieldValue = NonNullable<ItemV2['customFieldValues']>[string];

function toCustomFieldValueDto(
  entry: Record<string, unknown>,
  valueKey: string,
): CustomFieldValueDto {
  const dto: CustomFieldValueDto = {
    value: entry[valueKey] as string | number | boolean | null,
  };
  if (typeof entry.description === 'string') dto.description = entry.description;
  return dto;
}

export function customFieldV1ToV2(v1Value: ItemCustomFieldValue): V2FieldValue {
  if (v1Value === null || v1Value === undefined) return null;
  if (Array.isArray(v1Value)) {
    return (v1Value as Array<Record<string, unknown>>).map((entry) => {
      const valueKey = Object.keys(entry).find((k) => k !== 'id' && k !== 'description');
      if (valueKey === undefined) return { value: null };
      return toCustomFieldValueDto(entry, valueKey);
    });
  }
  const obj = v1Value as Record<string, unknown>;
  const valueKey = Object.keys(obj).find((k) => k !== 'description');
  if (valueKey === undefined) return { value: null };
  return toCustomFieldValueDto(obj, valueKey);
}

export function itemV1ToV2(item: Item): ItemV2 {
  const { customFields, ...rest } = item;
  const customFieldValues: ItemV2['customFieldValues'] = {};
  if (customFields) {
    for (const [key, value] of Object.entries(customFields)) {
      customFieldValues[key] = customFieldV1ToV2(value);
    }
  }
  return { ...rest, customFieldValues };
}

export function itemV2ToV1(
  item: ItemV2,
  fieldDefs: ReadonlyArray<{ id: number; type: CustomFieldTypes }>,
): Item {
  const { customFieldValues, ...rest } = item;
  const typeById = new Map(fieldDefs.map((f) => [String(f.id), f.type]));
  const customFields: ItemCustomFieldValues = {};
  if (customFieldValues) {
    for (const [key, rawValue] of Object.entries(customFieldValues)) {
      if (rawValue === null || rawValue === undefined) continue;
      const fieldType = typeById.get(key);
      if (!fieldType) continue;
      const wrapped = customFieldV2ToV1(rawValue, fieldType);
      if (wrapped !== undefined) {
        customFields[key] = wrapped;
      }
    }
  }
  return { ...rest, customFields };
}
